import { $createLinkNode, $isLinkNode } from "@lexical/link";
import { $createListItemNode, $createListNode, $isListItemNode, $isListNode } from "@lexical/list";
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from "@lexical/rich-text";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  type ElementNode,
  type LexicalNode,
} from "lexical";

import type { RTEChildDefs, RTEFormatOpts, RTELiDefs, RTENodeDefs, RTEPackDefs } from "../RichTextEditorProps";
import { INLINE_FORMAT_NAMES } from "../configs/formats";
import { parseComponentsEnvelope } from "./parseComponentsEnvelope";

const getTextFormats = (node: LexicalNode & { hasFormat(format: string): boolean }): ReadonlyArray<RTEFormatOpts> => {
  const formats: RTEFormatOpts[] = [];
  for (const format of INLINE_FORMAT_NAMES) {
    if (node.hasFormat(format)) formats.push(format);
  }
  return formats;
};

const serializeInlineNode = (node: LexicalNode): RTEChildDefs | null => {
  if ($isLineBreakNode(node)) return { type: "linebreak" };
  if ($isTextNode(node)) {
    return { type: "text", formats: getTextFormats(node), content: node.getTextContent() };
  }
  if ($isLinkNode(node)) {
    return { type: "link", href: node.getURL(), children: serializeElementChildren(node) };
  }
  return null;
};

const serializeElementChildren = (parent: ElementNode): ReadonlyArray<RTEChildDefs> => {
  const result: RTEChildDefs[] = [];
  for (const child of parent.getChildren()) {
    const node = serializeInlineNode(child);
    if (node !== null) result.push(node);
  }
  return result;
};

const serializeBlockNode = (node: LexicalNode): RTENodeDefs | null => {
  if ($isHeadingNode(node)) {
    const level = parseInt(node.getTag().slice(1), 10) as 1 | 2 | 3 | 4 | 5 | 6;
    return { type: "heading", level, children: serializeElementChildren(node) };
  }
  if ($isListNode(node)) {
    const listType = node.getListType() === "number" ? "number" : "bullet";
    const items: RTELiDefs[] = [];
    for (const child of node.getChildren()) {
      if ($isListItemNode(child)) {
        items.push({ type: "listitem", children: serializeElementChildren(child) });
      }
    }
    return { type: "list", listType, children: items };
  }
  if ($isElementNode(node)) {
    return { type: "paragraph", children: serializeElementChildren(node) };
  }
  return null;
};

export const serializeComponents = (): string => {
  const nodes: RTENodeDefs[] = [];
  for (const child of $getRoot().getChildren()) {
    const result = serializeBlockNode(child);
    if (result !== null) nodes.push(result);
  }
  const envelope: RTEPackDefs = { version: 1, nodes };
  return JSON.stringify(envelope);
};

const appendHydratedChildren = (parent: ElementNode, children: ReadonlyArray<RTEChildDefs>): void => {
  for (const child of children) {
    const lexicalChild = hydrateNode(child);
    if (lexicalChild !== null) parent.append(lexicalChild);
  }
};

const hydrateNode = (node: RTENodeDefs): LexicalNode | null => {
  switch (node.type) {
    case "linebreak":
      return $createLineBreakNode();
    case "text": {
      const textNode = $createTextNode(node.content);
      for (const format of node.formats) textNode.toggleFormat(format);
      return textNode;
    }
    case "link": {
      const linkNode = $createLinkNode(node.href);
      appendHydratedChildren(linkNode, node.children);
      return linkNode;
    }
    case "listitem": {
      const listItemNode = $createListItemNode();
      appendHydratedChildren(listItemNode, node.children);
      return listItemNode;
    }
    case "list": {
      const listNode = $createListNode(node.listType);
      for (const item of node.children) {
        const listItemNode = $createListItemNode();
        appendHydratedChildren(listItemNode, item.children);
        listNode.append(listItemNode);
      }
      return listNode;
    }
    case "heading": {
      const headingNode = $createHeadingNode(`h${node.level}` as HeadingTagType);
      appendHydratedChildren(headingNode, node.children);
      return headingNode;
    }
    case "paragraph": {
      const paragraphNode = $createParagraphNode();
      appendHydratedChildren(paragraphNode, node.children);
      return paragraphNode;
    }
    case "code": {
      const paragraphNode = $createParagraphNode();
      paragraphNode.append($createTextNode(node.content));
      return paragraphNode;
    }
    default:
      return null;
  }
};

export const hydrateComponents = (json: string): void => {
  const envelope = parseComponentsEnvelope(json);
  if (!envelope) return;
  const root = $getRoot();
  root.clear();
  for (const node of envelope.nodes) {
    const lexicalNode = hydrateNode(node);
    if (lexicalNode !== null) root.append(lexicalNode);
  }
};
